import { useEffect, useState, type ComponentType, type ReactElement } from 'react';
import {
    ActionIcon,
    Anchor,
    Box,
    Collapse,
    Group,
    Indicator,
    Loader,
    Popover,
    Progress,
    ScrollArea,
    Stack,
    Text,
    ThemeIcon,
    UnstyledButton,
} from '@mantine/core';
import { AlertTriangle, Ban, Bell, CheckCircle2, ChevronDown, RefreshCw, XCircle, type LucideProps } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { NotificationTrayViewModel, type ActivityItem } from '@/features/notifications/NotificationTrayViewModel';
import { type InAppNotification, type JobRunEntry, type JobRunStatus, isTerminalJob } from '@/features/notifications/NotificationTypes';
import { formatRelativeTime } from '@/shared/DateFormatting';

/**
 * The top-nav activity tray: one unified feed of the SaaS notifications and the
 * user's own background jobs — no tabs. A job that has fired a completion
 * notification is shown as that notification, expandable to reveal the job's
 * detail (status, counts, error, duration); a job still running shows on its own
 * with live progress until it does.
 *
 * The badge polls the unread count and active-job indicator; opening the tray
 * loads the feed and fast-polls jobs so a running sync's progress advances in view.
 */
export default function NotificationTrayButton(): ReactElement {
    const [opened, setOpened] = useState(false);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const vm = useViewModel(NotificationTrayViewModel);
    const navigate = useNavigate();

    useEffect(() => {
        vm.startPolling();
    }, [vm]);

    const setOpen = (next: boolean): void => {
        setOpened(next);
        vm.setTrayOpen(next);
    };

    const openNotification = (notification: InAppNotification): void => {
        void vm.markRead(notification.id);
        if (notification.ctaTarget) {
            setOpen(false);
            navigate(notification.ctaTarget);
        }
    };

    const openJob = (job: JobRunEntry): void => {
        const route = JOB_KIND_ROUTE[job.kind];
        if (!route) return;
        setOpen(false);
        navigate(route);
    };

    const toggleExpand = (id: string): void => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const unread = vm.unreadCount;
    const activeJobs = vm.activeJobCount;
    const showBadge = unread > 0 || activeJobs > 0;

    return (
        <Popover
            opened={opened}
            onChange={(next) => {
                if (!next) setOpen(false);
            }}
            position="bottom-end"
            width={380}
            shadow="md"
            radius="md"
            withinPortal
        >
            <Popover.Target>
                <Indicator
                    disabled={!showBadge}
                    label={unread > 0 ? (unread > 9 ? '9+' : unread) : undefined}
                    size={unread > 0 ? 16 : 10}
                    offset={6}
                    color={unread > 0 ? 'red' : 'blue'}
                    processing={unread === 0 && activeJobs > 0}
                >
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={36}
                        radius="xl"
                        aria-label={badgeLabel(unread, activeJobs)}
                        onClick={() => setOpen(!opened)}
                    >
                        <Bell size={18} />
                    </ActionIcon>
                </Indicator>
            </Popover.Target>
            <Popover.Dropdown p={0}>
                <ActivityFeed
                    vm={vm}
                    expanded={expanded}
                    onToggleExpand={toggleExpand}
                    onOpenNotification={openNotification}
                    onOpenJob={openJob}
                />
            </Popover.Dropdown>
        </Popover>
    );
}

function badgeLabel(unread: number, activeJobs: number): string {
    const parts: string[] = [];
    if (unread > 0) parts.push(`${unread} unread`);
    if (activeJobs > 0) parts.push(`${activeJobs} running job${activeJobs === 1 ? '' : 's'}`);
    return parts.length === 0 ? 'Activity' : `Activity — ${parts.join(', ')}`;
}

function ActivityFeed({
    vm,
    expanded,
    onToggleExpand,
    onOpenNotification,
    onOpenJob,
}: {
    vm: NotificationTrayViewModel;
    expanded: Set<string>;
    onToggleExpand: (id: string) => void;
    onOpenNotification: (notification: InAppNotification) => void;
    onOpenJob: (job: JobRunEntry) => void;
}): ReactElement {
    const items = vm.activityItems;

    if (vm.loading && items.length === 0) {
        return (
            <Stack align="center" py="lg">
                <Loader size="sm" />
            </Stack>
        );
    }
    if (vm.failed && items.length === 0) {
        return <EmptyTray title="Activity unavailable" detail="We couldn't reach the notification service." />;
    }
    if (items.length === 0) {
        return <EmptyTray title="Nothing here yet" detail="Notifications and background jobs you start show up here." />;
    }

    return (
        <>
            <Group justify="space-between" px="md" py={6} style={{ borderBottom: '1px solid var(--bm-color-border)' }}>
                <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    Activity
                </Text>
                {vm.unreadCount > 0 && (
                    <Anchor component="button" type="button" size="xs" onClick={() => void vm.markAllRead()}>
                        Mark all read
                    </Anchor>
                )}
            </Group>
            <ScrollArea.Autosize mah={440}>
                <Stack gap={0}>
                    {items.map((item) =>
                        item.kind === 'notification' ? (
                            <NotificationItem
                                key={item.id}
                                item={item}
                                expanded={expanded.has(item.id)}
                                onToggle={() => onToggleExpand(item.id)}
                                onOpen={() => onOpenNotification(item.notification)}
                                onOpenJob={onOpenJob}
                            />
                        ) : (
                            <JobRow key={item.id} job={item.job} onOpen={onOpenJob} />
                        ),
                    )}
                </Stack>
            </ScrollArea.Autosize>
        </>
    );
}

const JOB_STATUS_META: Record<JobRunStatus, { color: string; label: string; Icon: ComponentType<LucideProps> }> = {
    running: { color: 'blue', label: 'In progress', Icon: RefreshCw },
    succeeded: { color: 'green', label: 'Completed', Icon: CheckCircle2 },
    partial: { color: 'yellow', label: 'Completed with warnings', Icon: AlertTriangle },
    failed: { color: 'red', label: 'Failed', Icon: XCircle },
    canceled: { color: 'gray', label: 'Canceled', Icon: Ban },
};

const JOB_KIND_LABELS: Record<string, string> = {
    'license-sync': 'License sync',
    'directory-sync': 'Directory sync',
    'telemetry-etl': 'Telemetry ETL',
    'usage-backfill': 'Usage import',
    classification: 'Classification',
    insights: 'Insights',
};

const JOB_KIND_ROUTE: Record<string, string> = {
    insights: '/insights',
    classification: '/behavior',
    'usage-backfill': '/token-usage',
    'directory-sync': '/settings/directory',
    'license-sync': '/licenses',
};

function jobKindLabel(kind: string): string {
    if (JOB_KIND_LABELS[kind]) return JOB_KIND_LABELS[kind];
    const words = kind.split(/[-_\s]+/).filter(Boolean);
    if (words.length === 0) return 'Job';
    return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

function jobHeadline(job: JobRunEntry): string {
    const label = jobKindLabel(job.kind);
    return job.title ? `${label} — ${job.title}` : label;
}

function jobStatusText(job: JobRunEntry): string {
    if (!isTerminalJob(job.status)) {
        if (typeof job.progress === 'number' && typeof job.total === 'number' && job.total > 0) {
            return `${job.progress} of ${job.total}`;
        }
        return job.message ?? 'In progress';
    }
    if (job.status === 'failed' && job.error) return job.error;
    return job.message ?? JOB_STATUS_META[job.status].label;
}

/** A human duration for a finished job, or null while it is still running. */
function jobDuration(job: JobRunEntry): string | null {
    if (!job.completedAt) return null;
    const ms = new Date(job.completedAt).getTime() - new Date(job.startedAt).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    return remainder === 0 ? `${minutes}m` : `${minutes}m ${remainder}s`;
}

function EmptyTray({ title, detail }: { title: string; detail: string }): ReactElement {
    return (
        <Stack gap={4} align="center" py="lg">
            <Bell size={22} style={{ color: 'var(--bm-color-text-dimmed)' }} />
            <Text size="sm" fw={600} mt={6}>
                {title}
            </Text>
            <Text size="xs" c="dimmed" ta="center" px="md">
                {detail}
            </Text>
        </Stack>
    );
}

function NotificationItem({
    item,
    expanded,
    onToggle,
    onOpen,
    onOpenJob,
}: {
    item: Extract<ActivityItem, { kind: 'notification' }>;
    expanded: boolean;
    onToggle: () => void;
    onOpen: () => void;
    onOpenJob: (job: JobRunEntry) => void;
}): ReactElement {
    const { notification, jobs } = item;
    const unread = notification.readAt === null;
    const hasJobs = jobs.length > 0;
    const anyRunning = jobs.some((job) => !isTerminalJob(job.status));

    return (
        <Box style={{ borderBottom: '1px solid var(--bm-color-border)', background: unread ? 'var(--bm-color-surface)' : undefined }}>
            <Group gap={0} wrap="nowrap" align="stretch">
                <UnstyledButton onClick={onOpen} px="md" py="sm" style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                        {anyRunning ? (
                            <Loader size="xs" mt={4} />
                        ) : (
                            <Box
                                mt={6}
                                w={6}
                                h={6}
                                style={{ flexShrink: 0, borderRadius: '50%', background: unread ? 'var(--mantine-color-red-6)' : 'transparent' }}
                            />
                        )}
                        <Box style={{ minWidth: 0 }}>
                            <Text size="sm" fw={unread ? 600 : 500} lineClamp={1}>
                                {notification.title}
                            </Text>
                            <Text size="xs" c="dimmed" lineClamp={2}>
                                {notification.message}
                            </Text>
                            <Text size="xs" c="dimmed" mt={4}>
                                {formatRelativeTime(notification.createdAt)}
                                {notification.ctaLabel ? ` · ${notification.ctaLabel}` : ''}
                            </Text>
                        </Box>
                    </Group>
                </UnstyledButton>
                {hasJobs && (
                    <ActionIcon
                        variant="subtle"
                        color="gray"
                        radius={0}
                        aria-label={expanded ? 'Hide job detail' : 'Show job detail'}
                        onClick={(event) => {
                            event.stopPropagation();
                            onToggle();
                        }}
                        style={{ alignSelf: 'stretch', height: 'auto' }}
                    >
                        <ChevronDown size={16} style={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 150ms ease' }} />
                    </ActionIcon>
                )}
            </Group>
            {hasJobs && (
                <Collapse in={expanded}>
                    <Stack gap={0} pl={30} style={{ background: 'var(--bm-color-canvas)' }}>
                        {jobs.map((job) => (
                            <JobDetailRow key={job.jobRunId} job={job} onOpen={() => onOpenJob(job)} />
                        ))}
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
}

function JobDetailRow({ job, onOpen }: { job: JobRunEntry; onOpen: () => void }): ReactElement {
    const running = !isTerminalJob(job.status);
    const meta = JOB_STATUS_META[job.status];
    const duration = jobDuration(job);
    const linkable = Boolean(JOB_KIND_ROUTE[job.kind]);

    return (
        <Box px="md" py={8} style={{ borderTop: '1px solid var(--bm-color-border)' }}>
            <Group gap="xs" wrap="nowrap" align="flex-start">
                {running ? (
                    <Loader size="xs" mt={2} />
                ) : (
                    <meta.Icon size={14} style={{ color: `var(--mantine-color-${meta.color}-6)`, marginTop: 2, flexShrink: 0 }} />
                )}
                <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="xs" fw={600}>
                        {jobKindLabel(job.kind)}{' '}
                        <Text span c={running ? 'dimmed' : meta.color} fw={500} inherit>
                            · {meta.label}
                        </Text>
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={2} mt={2}>
                        {jobStatusText(job)}
                    </Text>
                    <Group gap="sm" mt={4}>
                        <Text size="xs" c="dimmed">
                            {formatRelativeTime(job.completedAt ?? job.startedAt)}
                            {duration ? ` · ran ${duration}` : ''}
                        </Text>
                        {linkable && (
                            <Anchor component="button" type="button" size="xs" onClick={onOpen}>
                                View →
                            </Anchor>
                        )}
                    </Group>
                </Box>
            </Group>
        </Box>
    );
}

function JobRow({ job, onOpen }: { job: JobRunEntry; onOpen: (job: JobRunEntry) => void }): ReactElement {
    const running = !isTerminalJob(job.status);
    const meta = JOB_STATUS_META[job.status];
    const hasBar = running && typeof job.total === 'number' && job.total > 0;
    const pct = hasBar ? Math.min(100, Math.round(((job.progress ?? 0) / (job.total as number)) * 100)) : 0;
    const linkable = Boolean(JOB_KIND_ROUTE[job.kind]);

    return (
        <UnstyledButton
            onClick={() => onOpen(job)}
            disabled={!linkable}
            w="100%"
            px="md"
            py="sm"
            style={{ borderBottom: '1px solid var(--bm-color-border)', cursor: linkable ? 'pointer' : 'default', textAlign: 'left' }}
        >
            <Group gap="sm" wrap="nowrap" align="flex-start">
                {running ? (
                    <Loader size="sm" mt={2} />
                ) : (
                    <ThemeIcon color={meta.color} variant="light" size="md" radius="xl">
                        <meta.Icon size={16} />
                    </ThemeIcon>
                )}
                <Box style={{ minWidth: 0, flex: 1 }}>
                    <Group justify="space-between" wrap="nowrap" gap="xs">
                        <Text size="sm" fw={500} lineClamp={1}>
                            {jobHeadline(job)}
                        </Text>
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                            {formatRelativeTime(job.completedAt ?? job.startedAt)}
                        </Text>
                    </Group>
                    {hasBar && <Progress value={pct} size="xs" mt={6} mb={2} color={meta.color} />}
                    {running && !hasBar && <Progress value={100} striped animated size="xs" mt={6} mb={2} color="blue" />}
                    <Text size="xs" c={running ? 'dimmed' : meta.color} lineClamp={2} mt={2}>
                        {jobStatusText(job)}
                    </Text>
                </Box>
            </Group>
        </UnstyledButton>
    );
}
